import { useCallback, useEffect, useRef } from "react";

// Save DEBOUNCE_MS after the last change, but never later than MAX_WAIT_MS after
// the first unsaved one, so a long typing run still persists on a steady cadence.
const DEBOUNCE_MS = 3000;
const MAX_WAIT_MS = 20000;

// `enabled` is checked when the timer fires, not when it's scheduled, so the
// change that arms autosave still gets saved by the cycle it triggered.
export function useAutosave(save: () => Promise<void>, enabled: boolean) {
    const saveRef = useRef(save);
    const enabledRef = useRef(enabled);
    saveRef.current = save;
    enabledRef.current = enabled;

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const firstChangeAtRef = useRef<number | null>(null);
    const inFlightRef = useRef(false);
    const pendingRef = useRef(false);
    // Lets run() re-arm without a circular dependency on schedule().
    const scheduleRef = useRef<() => void>(() => {});

    const cancel = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        firstChangeAtRef.current = null;
        pendingRef.current = false;
    }, []);

    const run = useCallback(async () => {
        timerRef.current = null;
        if (!enabledRef.current) {
            firstChangeAtRef.current = null;
            return;
        }
        // Re-arm after the in-flight save settles so two PUTs never race.
        if (inFlightRef.current) {
            pendingRef.current = true;
            return;
        }
        inFlightRef.current = true;
        firstChangeAtRef.current = null;
        try {
            await saveRef.current();
        } finally {
            inFlightRef.current = false;
            if (pendingRef.current) {
                pendingRef.current = false;
                scheduleRef.current();
            }
        }
    }, []);

    const schedule = useCallback(() => {
        const now = Date.now();
        if (firstChangeAtRef.current == null) firstChangeAtRef.current = now;
        const untilMaxWait = MAX_WAIT_MS - (now - firstChangeAtRef.current);
        const delay = Math.max(0, Math.min(DEBOUNCE_MS, untilMaxWait));
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => { void run(); }, delay);
    }, [run]);
    scheduleRef.current = schedule;

    // Skips the remaining debounce. Only fires when a cycle was armed, so it stays a
    // no-op after cancel() — publishing and an explicit save must not PUT twice.
    const flush = useCallback(() => {
        const armed = timerRef.current !== null || pendingRef.current;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        firstChangeAtRef.current = null;
        pendingRef.current = false;
        if (!armed || !enabledRef.current || inFlightRef.current) return;
        void saveRef.current();
    }, []);

    // Navigating to another page tears the editor down without going through its
    // close button, so unmount has to save rather than cancel.
    useEffect(() => flush, [flush]);

    return { schedule, cancel, flush };
}
